/**
 * Pipedrive Person Detail Page
 * Shows person details with related deals, activities, and notes
 */

import { useParams, Link } from 'react-router-dom'
import { usePipedrivePerson } from '@/hooks/use-pipedrive-api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  User,
  Building2,
  Briefcase,
  Calendar,
  StickyNote,
  ArrowLeft,
  Mail,
  Phone,
  ExternalLink,
} from 'lucide-react'
import { CATEGORICAL, SEMANTIC } from '@/lib/theme'

export default function PipedrivePersonDetail() {
  const { id } = useParams<{ id: string }>()
  const { data: person, isLoading, error } = usePipedrivePerson(id ?? '')

  const getStatusStyle = (status: string | null): React.CSSProperties => {
    switch (status) {
      case 'won':
        return { backgroundColor: SEMANTIC.success.fill, color: SEMANTIC.success.text }
      case 'lost':
        return { backgroundColor: SEMANTIC.error.fill, color: SEMANTIC.error.text }
      case 'open':
        return { backgroundColor: SEMANTIC.info.fill, color: SEMANTIC.info.text }
      default:
        return { backgroundColor: SEMANTIC.neutral.fill, color: SEMANTIC.neutral.text }
    }
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          Failed to load person. Please try again later.
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    )
  }

  if (!person) {
    return (
      <div className="p-6">
        <div className="bg-muted p-4 rounded-lg">Person not found.</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/pipedrive/people">
          <Button variant="ghost" size="icon" aria-label="Back to people">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <User className="h-6 w-6" aria-hidden="true" />
            {person.name}
          </h1>
          <p className="text-muted-foreground">
            {person.job_title || 'No job title'} {person.owner ? `• Owner: ${person.owner}` : ''}
          </p>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {person.email_work && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Work:</span>
                <a href={`mailto:${person.email_work}`} className="text-primary hover:underline">
                  {person.email_work}
                </a>
              </div>
            )}
            {person.email_personal && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Personal:</span>
                <a
                  href={`mailto:${person.email_personal}`}
                  className="text-primary hover:underline"
                >
                  {person.email_personal}
                </a>
              </div>
            )}
            {person.phone_work && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Work:</span>
                <a href={`tel:${person.phone_work}`} className="text-primary hover:underline">
                  {person.phone_work}
                </a>
              </div>
            )}
            {person.phone_mobile && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Mobile:</span>
                <a href={`tel:${person.phone_mobile}`} className="text-primary hover:underline">
                  {person.phone_mobile}
                </a>
              </div>
            )}
            {!person.email_work &&
              !person.email_personal &&
              !person.phone_work &&
              !person.phone_mobile && (
                <p className="text-muted-foreground">No contact information available</p>
              )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Organization & Statistics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {person.organization_id ? (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Organization:</span>
                <Link
                  to={`/pipedrive/organizations/${person.organization_id}`}
                  className="text-primary hover:underline flex items-center gap-1"
                >
                  {person.organization || 'View Organization'}
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            ) : person.organization ? (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Organization:</span>
                <span>{person.organization}</span>
              </div>
            ) : null}
            <div className="pt-4 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Open Deals</p>
                  <p className="text-2xl font-bold">{person.open_deals_count ?? 0}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Closed Deals</p>
                  <p className="text-2xl font-bold" style={{ color: CATEGORICAL.evergreen.solid }}>
                    {person.closed_deals_count ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Activities</p>
                  <p className="text-2xl font-bold">{person.activities_count ?? 0}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold" style={{ color: CATEGORICAL.evergreen.solid }}>
                    {person.done_activities_count ?? 0}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Related Records Tabs */}
      <Tabs defaultValue="deals">
        <TabsList>
          <TabsTrigger value="deals" className="flex items-center gap-2">
            <Briefcase className="h-4 w-4" style={{ color: CATEGORICAL.navy.solid }} />
            Deals ({person.deals?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="activities" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" style={{ color: CATEGORICAL.evergreen.solid }} />
            Activities ({person.activities?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-2">
            <StickyNote className="h-4 w-4" style={{ color: CATEGORICAL.steel.solid }} />
            Notes ({person.notes?.length ?? 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="deals">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {person.deals?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No deals found
                      </TableCell>
                    </TableRow>
                  ) : (
                    person.deals?.map((deal) => (
                      <TableRow key={deal.id}>
                        <TableCell>
                          <Link
                            to={`/pipedrive/deals/${deal.id}`}
                            className="font-medium text-primary hover:underline flex items-center gap-1"
                          >
                            {deal.title}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge style={getStatusStyle(deal.status)} variant="secondary">
                            {deal.status || 'Unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell>{deal.stage_name || '-'}</TableCell>
                        <TableCell>{deal.owner || '-'}</TableCell>
                        <TableCell className="text-right font-medium">
                          {deal.value ? `$${deal.value.toLocaleString()}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activities">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Due Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {person.activities?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No activities found
                      </TableCell>
                    </TableRow>
                  ) : (
                    person.activities?.map((activity) => (
                      <TableRow key={activity.id}>
                        <TableCell className="font-medium">
                          <Link
                            to={`/pipedrive/activities/${activity.id}`}
                            className="text-primary hover:underline"
                          >
                            {activity.subject || '-'}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{activity.type || 'Unknown'}</Badge>
                        </TableCell>
                        <TableCell>{activity.assigned_to || '-'}</TableCell>
                        <TableCell>
                          <span
                            style={{
                              color:
                                activity.done === 'Yes'
                                  ? CATEGORICAL.evergreen.solid
                                  : CATEGORICAL.navy.solid,
                            }}
                          >
                            {activity.done === 'Yes' ? 'Done' : 'Pending'}
                          </span>
                        </TableCell>
                        <TableCell>{activity.due_date || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Content</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {person.notes?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                        No notes found
                      </TableCell>
                    </TableRow>
                  ) : (
                    person.notes?.map((note) => (
                      <TableRow key={note.id}>
                        <TableCell className="max-w-md">
                          <Link
                            to={`/pipedrive/notes/${note.id}`}
                            className="text-primary hover:underline line-clamp-2"
                          >
                            {note.content || '-'}
                          </Link>
                        </TableCell>
                        <TableCell>{note.user_name || '-'}</TableCell>
                        <TableCell>{note.add_time || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Metadata */}
      {(person.created_date || person.update_time) && (
        <div className="text-sm text-muted-foreground">
          {person.created_date && <span>Created: {person.created_date}</span>}
          {person.created_date && person.update_time && <span className="mx-2">•</span>}
          {person.update_time && <span>Updated: {person.update_time}</span>}
        </div>
      )}
    </div>
  )
}
